import { Owner, POOL_CAPACITY, ProjectileFlags, ProjectileKind } from '../constants';
import { IndexPool } from './IndexPool';

export class ProjectilePool {
  readonly indices = new IndexPool(POOL_CAPACITY.projectiles);
  readonly x = new Float32Array(POOL_CAPACITY.projectiles);
  readonly y = new Float32Array(POOL_CAPACITY.projectiles);
  readonly prevX = new Float32Array(POOL_CAPACITY.projectiles);
  readonly prevY = new Float32Array(POOL_CAPACITY.projectiles);
  readonly x2 = new Float32Array(POOL_CAPACITY.projectiles);
  readonly y2 = new Float32Array(POOL_CAPACITY.projectiles);
  readonly vx = new Float32Array(POOL_CAPACITY.projectiles);
  readonly vy = new Float32Array(POOL_CAPACITY.projectiles);
  readonly radius = new Float32Array(POOL_CAPACITY.projectiles);
  readonly life = new Float32Array(POOL_CAPACITY.projectiles);
  readonly damage = new Float32Array(POOL_CAPACITY.projectiles);
  readonly angle = new Float32Array(POOL_CAPACITY.projectiles);
  readonly age = new Float32Array(POOL_CAPACITY.projectiles);
  readonly aux0 = new Float32Array(POOL_CAPACITY.projectiles);
  readonly aux1 = new Float32Array(POOL_CAPACITY.projectiles);
  readonly owner = new Uint8Array(POOL_CAPACITY.projectiles);
  readonly kind = new Uint8Array(POOL_CAPACITY.projectiles);
  readonly flags = new Uint8Array(POOL_CAPACITY.projectiles);
  readonly chainDepth = new Uint8Array(POOL_CAPACITY.projectiles);
  readonly source = new Uint8Array(POOL_CAPACITY.projectiles);
  readonly attackId = new Uint32Array(POOL_CAPACITY.projectiles);
  overflowDrops = 0;

  spawn(
    x: number,
    y: number,
    vx: number,
    vy: number,
    radius: number,
    life: number,
    damage: number,
    owner: Owner,
    kind: ProjectileKind,
    flags = ProjectileFlags.None,
    attackId = 0,
    source = 0,
    chainDepth = 0,
    x2 = x,
    y2 = y,
  ): number {
    const index = this.indices.acquire();
    if (index < 0) {
      this.overflowDrops += 1;
      return -1;
    }
    this.x[index] = x;
    this.y[index] = y;
    this.prevX[index] = x;
    this.prevY[index] = y;
    this.x2[index] = x2;
    this.y2[index] = y2;
    this.vx[index] = vx;
    this.vy[index] = vy;
    this.radius[index] = radius;
    this.life[index] = life;
    this.damage[index] = damage;
    this.owner[index] = owner;
    this.kind[index] = kind;
    this.flags[index] = flags;
    this.attackId[index] = attackId >>> 0;
    this.source[index] = source;
    this.chainDepth[index] = chainDepth;
    this.angle[index] = Math.atan2(vy, vx);
    this.age[index] = 0;
    this.aux0[index] = 0;
    this.aux1[index] = 0;
    return index;
  }

  release(index: number): boolean {
    return this.indices.release(index);
  }

  clearOwner(owner: Owner): void {
    const active = this.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] !== 0 && this.owner[i] === owner) this.indices.release(i);
    }
  }

  update(timeScaleEnemyProjectiles: number): void {
    // HOTPATH-BEGIN
    const active = this.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0) continue;
      this.prevX[i] = this.x[i];
      this.prevY[i] = this.y[i];
      const scale = this.owner[i] === Owner.Enemy ? timeScaleEnemyProjectiles : 1;
      this.x[i] += this.vx[i] * scale;
      this.y[i] += this.vy[i] * scale;
      if ((this.flags[i] & ProjectileFlags.Segment) !== 0) {
        this.x2[i] += this.vx[i] * scale;
        this.y2[i] += this.vy[i] * scale;
      }
      this.age[i] += scale;
      this.life[i] -= scale;
      if (this.life[i] <= 0 || this.x[i] < -48 || this.x[i] > 432 || this.y[i] < -56 || this.y[i] > 272) {
        this.indices.release(i);
      }
    }
    // HOTPATH-END
  }

  reset(): void {
    this.indices.reset();
    this.overflowDrops = 0;
  }
}
